import { useEffect, useState } from 'react'
import { ApiError, api } from '@api'
import {
	APPLE_SIGN_IN,
	prepareAppleSignIn,
	signInWithApple,
} from '@auth/apple.ts'
import { useAuth } from '@auth/AuthContext.tsx'
import {
	clearSignupIntent,
	setSignupIntent,
	setSuggestedName,
} from '@auth/intent.ts'
import { Field } from '@components/Field.tsx'
import { ProviderButton } from '@components/ProviderButton.tsx'
import styles from './SignIn.module.css'

// What the person came here to do. 'create' and 'join' both register a new
// account — they differ only in what Onboarding asks for afterwards.
type Path = 'signin' | 'create' | 'join'

// Deliberately stepped rather than one clever form: the first screen says
// plainly whether you're signing in or registering, so nobody meets the
// "send me a code" button while holding a family code and wonders which is
// which.
type Stage =
	| { kind: 'welcome' }
	| { kind: 'register' }
	| { kind: 'email'; path: Path }
	| { kind: 'code'; path: Path; email: string }

const messageFor = (e: unknown): string => {
	if (e instanceof ApiError) {
		if (e.code === 'registration_closed') {
			return `Hewey isn't open for new sign-ups right now. If you already have an account, sign in with that email instead.`
		}
		if (e.code === 'unknown_email') {
			return `We can't find an account for that email. Check it for typos, or go back and create an account.`
		}
		if (e.code === 'unknown_apple_id') {
			return `There's no Hewey account for this Apple ID. If you already use Hewey with a different email, sign in with that email instead, or go back and create an account.`
		}
		if (e.code === 'apple_id_mismatch') {
			return `The Hewey account for this email is linked to a different Apple ID. Sign in with that Apple ID, or with your email instead.`
		}
		return e.message
	}
	return 'Something went wrong'
}

const titleFor = (stage: Stage): string => {
	if (stage.kind === 'welcome') return 'Hewey'
	if (stage.kind === 'register') return 'Create an account'
	return stage.path === 'signin' ? 'Sign in' : 'Create an account'
}

const emailHintFor = (path: Path): string => {
	const lead = path === 'signin' ? '' : `Let's set up your account first. `
	const how = APPLE_SIGN_IN
		? `Continue with Apple, or enter your email and we'll send you a 5-digit sign-in code.`
		: path === 'signin'
			? `Enter your email and we'll send you a 5-digit sign-in code.`
			: `We'll email a 5-digit sign-in code to confirm your address.`
	const tail =
		path === 'join' ? ` You'll enter your family code straight after.` : ''
	return lead + how + tail
}

type ChoiceProps = { title: string; desc: string; onClick: () => void }

const Choice = ({ title, desc, onClick }: ChoiceProps) => (
	<button type="button" className={styles.choice} onClick={onClick}>
		<span className={styles.choiceTitle}>{title}</span>
		<span className={styles.choiceDesc}>{desc}</span>
	</button>
)

export const SignIn = () => {
	const { setSession } = useAuth()
	const [stage, setStage] = useState<Stage>({ kind: 'welcome' })
	const [email, setEmail] = useState('')
	const [code, setCode] = useState('')
	// Which action is in flight. Every button waits on it, but only that
	// action's button says so.
	const [busy, setBusy] = useState<'email' | 'apple' | 'code' | null>(null)
	const [error, setError] = useState<string | null>(null)

	useEffect(prepareAppleSignIn, [])

	const go = (next: Stage) => {
		setError(null)
		setStage(next)
	}

	// Record the choice before the email round-trip — Onboarding reads it once
	// the session lands, by which point this component is long gone.
	const start = (path: Path) => {
		if (path === 'signin') clearSignupIntent()
		else setSignupIntent(path)
		go({ kind: 'email', path })
	}

	const requestCode = async (e: React.FormEvent) => {
		e.preventDefault()
		if (stage.kind !== 'email') return
		setBusy('email')
		setError(null)
		const normalized = email.trim().toLowerCase()
		try {
			await api.authRequest({
				email: normalized,
				intent: stage.path === 'signin' ? 'signin' : 'register',
			})
			setStage({ kind: 'code', path: stage.path, email: normalized })
			setCode('')
		} catch (e) {
			setError(messageFor(e))
		} finally {
			setBusy(null)
		}
	}

	const continueWithApple = async () => {
		if (stage.kind !== 'email') return
		setBusy('apple')
		setError(null)
		try {
			const apple = await signInWithApple()
			if (!apple) return
			setSuggestedName(apple.givenName)
			const res = await api.authApple({
				code: apple.code,
				client: apple.client,
				intent: stage.path === 'signin' ? 'signin' : 'register',
			})
			setSession(res.user, res.household)
		} catch (e) {
			setError(messageFor(e))
		} finally {
			setBusy(null)
		}
	}

	const verifyCode = async (e: React.FormEvent) => {
		e.preventDefault()
		if (stage.kind !== 'code') return
		setBusy('code')
		setError(null)
		try {
			const res = await api.authVerify({ email: stage.email, code })
			setSession(res.user, res.household)
		} catch (e) {
			setError(messageFor(e))
		} finally {
			setBusy(null)
		}
	}

	return (
		<main className={styles.screen}>
			<div className={styles.card}>
				<div className={styles.brand}>
					<img src="/logo.png" alt="" className={styles.mark} />
					<h1 className={styles.title}>{titleFor(stage)}</h1>
				</div>

				{stage.kind === 'welcome' ? (
					<div className={styles.choices}>
						<Choice
							title="Sign in"
							desc="You've used Hewey before."
							onClick={() => start('signin')}
						/>
						<Choice
							title="Create an account"
							desc="You're new here."
							onClick={() => go({ kind: 'register' })}
						/>
					</div>
				) : null}

				{stage.kind === 'register' ? (
					<>
						<p className={styles.hint}>
							Are you starting fresh, or joining family who
							already use Hewey?
						</p>
						<div className={styles.choices}>
							<Choice
								title="Start a new household"
								desc="You'll name it, then invite your family with a code."
								onClick={() => start('create')}
							/>
							<Choice
								title="Join a family"
								desc="Someone already using Hewey gave you a 6-character family code."
								onClick={() => start('join')}
							/>
						</div>
						<button
							type="button"
							className={styles.linkButton}
							onClick={() => go({ kind: 'welcome' })}
						>
							Back
						</button>
					</>
				) : null}

				{stage.kind === 'email' ? (
					<form onSubmit={requestCode} className={styles.form}>
						<p className={styles.hint}>
							{emailHintFor(stage.path)}
						</p>
						{APPLE_SIGN_IN ? (
							<>
								<ProviderButton
									provider="apple"
									disabled={busy !== null}
									onClick={() => void continueWithApple()}
								/>
								<p className={styles.divider}>or</p>
							</>
						) : null}
						<Field label="Your email">
							<input
								type="email"
								// Not with the Apple button: on a phone the
								// keyboard would hide it.
								autoFocus={!APPLE_SIGN_IN}
								required
								autoComplete="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								placeholder="you@example.com"
							/>
						</Field>
						<button
							type="submit"
							className={styles.button}
							disabled={busy !== null || !email}
						>
							{busy === 'email'
								? 'Sending…'
								: 'Email me a sign-in code'}
						</button>
						<button
							type="button"
							className={styles.linkButton}
							onClick={() =>
								go(
									stage.path === 'signin'
										? { kind: 'welcome' }
										: { kind: 'register' },
								)
							}
						>
							Back
						</button>
					</form>
				) : null}

				{stage.kind === 'code' ? (
					<form onSubmit={verifyCode} className={styles.form}>
						<p className={styles.hint}>
							We emailed a 5-digit sign-in code to{' '}
							<strong>{stage.email}</strong>.
						</p>
						<Field label="Sign-in code">
							<input
								type="text"
								autoFocus
								required
								inputMode="numeric"
								pattern="\d{5}"
								maxLength={5}
								autoComplete="one-time-code"
								value={code}
								onChange={(e) =>
									setCode(e.target.value.replace(/\D/g, ''))
								}
								className={styles.codeInput}
								placeholder="·····"
							/>
						</Field>
						<button
							type="submit"
							className={styles.button}
							disabled={busy !== null || code.length !== 5}
						>
							{busy === 'code' ? 'Verifying…' : 'Continue'}
						</button>
						<button
							type="button"
							className={styles.linkButton}
							onClick={() =>
								go({ kind: 'email', path: stage.path })
							}
						>
							Use a different email
						</button>
					</form>
				) : null}

				{error ? <p className={styles.error}>{error}</p> : null}
			</div>
		</main>
	)
}
